import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,

    Button,
    Snackbar,
    Alert,
    CircularProgress,
    Stack,
    Container,
    Paper,
    AlertColor,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import OrganizationService from '../../services/OrganizationService';
import { OrganizationUpdateDTO, OrganizationCreateDTO } from '../../types/Organization';
import OrganizationDetailsPanel from './OrganizationDetailsPanel';
import OrganizationUsersPanel from './OrganizationUsersPanel';

const OrganizationProfile: React.FC = () => {
    const navigate = useNavigate();
    const params = useParams<{ id: string }>();


    const [organizationData, setOrganizationData] = useState<OrganizationUpdateDTO>({});
    const [initialOrganizationData, setInitialOrganizationData] = useState<OrganizationUpdateDTO>({});

    const [isNew, setIsNew] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [formChanged, setFormChanged] = useState<boolean>(false);
    const [notification, setNotification] = useState<{ message: string; severity: AlertColor } | null>(null);
    const [expandedPanel, setExpandedPanel] = useState<string | false>('details');

    const id = params.id;

    const handlePanelChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedPanel(isExpanded ? panel : false);
    };

    const fetchOrganization = useCallback(async () => {
        console.log('[OrganizationProfile] fetchOrganization called with id:', id);
        if (id) {
            try {
                console.log('[OrganizationProfile] Fetching organization data for id:', id);
                const data = await OrganizationService.getOrganization(id);
                console.log('[OrganizationProfile] Successfully fetched organization data:', data);

                const initialData = {
                    name: data.name,
                    street_address: data.street_address,
                    city: data.city,
                    state: data.state,
                    zip_code: data.zip_code,
                    phone: data.phone,
                    website: data.website,
                    mission: data.mission,
                    vision: data.vision,
                };
                setOrganizationData(initialData);
                setInitialOrganizationData(initialData);
                setIsNew(false);
            } catch (error: any) {
                console.error('[OrganizationProfile] Error fetching organization:', error);
                console.error('[OrganizationProfile] Error details:', {
                    message: error?.message,
                    status: error?.response?.status,
                    statusText: error?.response?.statusText,
                    data: error?.response?.data
                });
                
                // Check if it's an authorization error that might cause redirect
                if (error?.response?.status === 401 || error?.response?.status === 403) {
                    console.error('[OrganizationProfile] Authorization error - this might cause redirect');
                    // Don't redirect here, let the error boundary or auth system handle it
                }
                
                setNotification({ message: "Error fetching organization data.", severity: "error" });
            }
        } else {
            setIsNew(true);
            const initialData = {
                name: "",
                street_address: "",
                city: "",
                state: "",
                zip_code: "",
                phone: "",
                website: "",
                mission: "",
                vision: "",
            };
            setOrganizationData(initialData);
            setInitialOrganizationData(initialData);
        }
        setIsLoading(false);
    }, [id]);

    useEffect(() => {
        fetchOrganization();
    }, [fetchOrganization]);

    useEffect(() => {
        setFormChanged(JSON.stringify(organizationData) !== JSON.stringify(initialOrganizationData));
    }, [organizationData, initialOrganizationData]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;

        if (name === "zip_code") {
            if (/^[0-9]*$/.test(value)) {
                setOrganizationData((prev) => ({ ...prev, [name]: value }));
            }
        } else if (name === "phone") {
            const onlyNums = value.replace(/[^0-9]/g, '');
            if (onlyNums.length <= 10) {
                let formattedNumber = onlyNums;
                if (onlyNums.length > 3 && onlyNums.length <= 6) {
                    formattedNumber = `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3)}`;
                } else if (onlyNums.length > 6) {
                    formattedNumber = `(${onlyNums.slice(0, 3)}) ${onlyNums.slice(3, 6)}-${onlyNums.slice(6, 10)}`;
                } else if (onlyNums.length > 0) {
                    formattedNumber = `(${onlyNums}`
                }
                setOrganizationData((prev) => ({ ...prev, [name]: formattedNumber }));
            }
        } else {
            setOrganizationData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        try {
            let savedOrganization;
            if (isNew) {
                if (!organizationData.name) {
                    throw new Error("Organization name is required.");
                }
                savedOrganization = await OrganizationService.createOrganization(organizationData as OrganizationCreateDTO);
                setNotification({ message: "Organization created successfully!", severity: "success" });
                navigate(`/organizations/${savedOrganization.id}`);
            } else if (id) {
                await OrganizationService.updateOrganization(id, organizationData);
                savedOrganization = await OrganizationService.getOrganization(id);
                setNotification({ message: "Organization updated successfully!", severity: "success" });
            }
            if (savedOrganization) {
                const initialData = {
                    name: savedOrganization.name,
                    street_address: savedOrganization.street_address,
                    city: savedOrganization.city,
                    state: savedOrganization.state,
                    zip_code: savedOrganization.zip_code,
                    phone: savedOrganization.phone,
                    website: savedOrganization.website,
                    mission: savedOrganization.mission,
                    vision: savedOrganization.vision,
                };

                setOrganizationData(initialData);
                setInitialOrganizationData(initialData);
            }
        } catch (error) {
            console.error("Error saving organization:", error);
            setNotification({ message: "Error saving organization.", severity: "error" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        setOrganizationData(initialOrganizationData);
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Container maxWidth="lg">
            <Paper sx={{ p: 4, mt: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                    <Button startIcon={<ArrowBackIcon />} onClick={handleBack}>
                        Back
                    </Button>

                    <Box>
                        <Button
                            variant="outlined"
                            onClick={handleCancel}
                            sx={{ mr: 2 }}
                            disabled={!formChanged || isSaving}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={!formChanged || isSaving}
                        >
                            {isSaving ? <CircularProgress size={24} /> : "Save"}
                        </Button>
                    </Box>
                </Stack>

                <Box sx={{ mb: 2 }}>
                    <OrganizationDetailsPanel
                        expanded={expandedPanel === 'details'}
                        onToggle={handlePanelChange('details')}
                        organizationData={organizationData}
                        handleInputChange={handleInputChange}
                    />
                </Box>
                {id && (
                    <>
                        <Box sx={{ mb: 2 }}>
                            <OrganizationUsersPanel
                                expanded={expandedPanel === 'users'}
                                onToggle={handlePanelChange('users')}
                                organizationId={id}
                            />
                        </Box>
                    </>
                )}

            </Paper>

            {notification && (
                <Snackbar
                    open
                    autoHideDuration={6000}
                    onClose={() => setNotification(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={() => setNotification(null)} severity={notification.severity} sx={{ width: '100%' }}>
                        {notification.message}
                    </Alert>
                </Snackbar>
            )}
        </Container>
    );
};

export default OrganizationProfile;
